<template>
  <div class="page-nav">
    <ul v-if="total === 0" class="pagination">
      <li class="location">
        <a aria-label="Previous">
          <span aria-hidden="true">&laquo;</span>
        </a>
        <a aria-label="Previous">
          <span aria-hidden="true">&lsaquo;</span>
        </a>
      </li>
      <li class="active">
        <a>1</a>
      </li>
      <li class="location">
        <a aria-label="Next">
          <span aria-hidden="true">&rsaquo;</span>
        </a>
        <a aria-label="Next">
          <span aria-hidden="true">&raquo;</span>
        </a>
      </li>
    </ul>

    <ul v-if="total !== 0" class="pagination">
      <li :class="current + pageNum === 1 ? 'location' : ''">
        <slot name="prePre">
          <a
            v-if="doubleTab === true"
            aria-label="Previous"
            @click="prev(current, $event)"
          >
            <span aria-hidden="true">&laquo;</span>
          </a>
          <a v-if="doubleTab === false">&laquo;</a>
        </slot>
        <slot name="pre">
          <a aria-label="Previous" @click="fn(current - 1, $event, 1)">
            <span aria-hidden="true">&lsaquo;</span>
          </a>
        </slot>
      </li>
      <li v-for="p in pageCounts" :key="p" :class="p === c ? 'active' : ''">
        <a @click="p === c ? null : fn(p, $event, 2)">{{ p + pageNum }}</a>
      </li>
      <li :class="current === total ? 'location' : ''">
        <slot name="Next">
          <a aria-label="Next" @click="fn(current + 1, $event, 3)">
            <span aria-hidden="true">&rsaquo;</span>
          </a>
        </slot>
        <slot name="NextNext">
          <a
            v-if="doubleTab === true"
            aria-label="Next"
            @click="next(current + limit, $event)"
          >
            <span aria-hidden="true">&raquo;</span>
          </a>

          <a v-if="doubleTab === false">&raquo;</a>
        </slot>
      </li>
    </ul>
  </div>
</template>

<script>
import { computed, ref, watch } from "vue";

export default {
  props: {
    value: {
      type: Number,
      default: 0,
    },
    perPage: {
      type: Number,
      default: 10,
    },
    limit: {
      type: Number,
      default: 10,
    },
    totalRows: {
      type: Number,
      default: 1,
    },
    disabled: {
      type: Boolean,
      default: true,
    },
    tab: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["input", "inputWatch"],
  setup(props, { emit }) {
    const pageNum = ref(0);
    const current = ref(props.value);
    const doubleTab = ref(props.tab);

    const total = computed(() => {
      return Math.ceil(props.totalRows / props.perPage);
    });

    const c = computed(() => {
      const cValue = current.value < 1 ? 1 : current.value;
      return cValue > total.value ? cValue - pageNum.value : cValue;
    });

    const start = computed(() => {
      let startValue = c.value - (c.value % props.limit);
      if (c.value % props.limit === 0) {
        startValue = c.value - props.limit;
      }
      if (c.value > props.limit) startValue += 1;
      return Math.max(1, startValue);
    });

    const end = computed(() => {
      let endValue = start.value + props.limit - 1;
      if (c.value % props.limit === 0) endValue = c.value;
      return Math.min(endValue, total.value);
    });

    const pageCounts = computed(() => {
      const pages = [];
      for (let i = start.value; i <= end.value; i++) {
        pages.push(i);
      }
      return pages;
    });

    watch(
      () => props.value,
      (newValue) => {
        if (newValue === -1) {
          current.value = 1;
          pageNum.value = 0;
        } else {
          current.value = newValue;
        }
        emit("inputWatch", current.value);
      },
      { immediate: true }
    );

    watch(
      () => props.totalRows,
      () => {
        const tmp = Math.ceil(props.totalRows / props.perPage);
        if (
          props.totalRows < props.perPage * props.limit &&
          current.value > tmp
        ) {
          current.value = tmp;
        }
      }
    );

    watch(
      () => props.tab,
      () => {
        doubleTab.value = props.tab;
      }
    );

    const prev = (p) => {
      if (p < 1) {
        current.value = 1;
      } else {
        current.value = p - props.limit;
      }
      if (current.value <= 10 && pageNum.value > 0) {
        current.value += props.limit;
        pageNum.value -= props.limit;
      } else {
        current.value = 1;
      }
      emit("input", current.value, pageNum.value);
    };

    const next = (p) => {
      if (props.totalRows < props.perPage * props.limit + 1) {
        current.value = Math.ceil(props.totalRows / props.perPage);
        emit("input", current.value, pageNum.value);
        return;
      }
      current.value = p;

      if (current.value > 9) {
        current.value -= props.limit;
        pageNum.value += props.limit;
      }
      emit("input", current.value, pageNum.value);
    };

    const fn = (p, event, type) => {
      current.value = p;

      if (
        type === 1 &&
        current.value % props.limit === 0 &&
        current.value < 10 &&
        pageNum.value > 0
      ) {
        current.value = 10;
        pageNum.value -= props.limit;
      }

      if (
        type === 3 &&
        current.value % props.limit === 1 &&
        current.value > 10
      ) {
        current.value = 1;
        pageNum.value += props.limit;
      }

      emit("input", current.value, pageNum.value);
    };

    return {
      pageNum,
      current,
      doubleTab,
      total,
      c,
      start,
      end,
      pageCounts,
      prev,
      next,
      fn,
    };
  },
};
</script>

<style scoped>
.pagination {
  display: inline-block;
  padding-left: 0;
  margin: 30px 0;
  border-radius: 4px;
  font-size: 1rem;
  font-family: Arial, Helvetica, sans-serif;
}
.pagination > li {
  display: inline;
}
.pagination > li > a,
.pagination > li > span {
  position: relative;
  float: left;
  padding: 6px 12px;
  margin-left: -1px;
  line-height: 1.42857143;
  /* color: #337ab7; */
  color: #707070;
  text-decoration: none;
  background-color: #fff;
  border: 1px solid #ddd;
  cursor: pointer;
}

.pagination > li > a:hover,
.pagination > li > span:hover,
.pagination > li > a:focus,
.pagination > li > span:focus {
  z-index: 2;
  /* color: #23527c; */
  color: #013b79;
  background-color: #eee;
  border-color: #ddd;
}
.pagination > .location > a {
  color: black;
  pointer-events: none;
  cursor: default;
}
.pagination > .active > a,
.pagination > .active > span,
.pagination > .active > a:hover,
.pagination > .active > span:hover,
.pagination > .active > a:focus,
.pagination > .active > span:focus {
  z-index: 3;
  color: #242424;
  font-weight: 700;
  cursor: default;
  background-color: #f4f4f4;
  border-color: #ddd;
}
.pagination > .disabled > span,
.pagination > .disabled > span:hover,
.pagination > .disabled > span:focus,
.pagination > .disabled > a,
.pagination > .disabled > a:hover,
.pagination > .disabled > a:focus {
  color: #777;
  cursor: not-allowed;
  background-color: #fff;
  border-color: #ddd;
}
.pagination-lg > li > a,
.pagination-lg > li > span {
  padding: 10px 16px;
  font-size: 18px;
  line-height: 1.3333333;
}

.pagination-sm > li > a,
.pagination-sm > li > span {
  padding: 5px 10px;
  font-size: 12px;
  line-height: 1.5;
}
</style>
